import React, { useEffect, useRef, useState } from 'react';

/**
 * Scroll reveal: fade + rise once on entry, using the `slideUp` keyframe that
 * already exists in src/index.css (no animation library, and none should be
 * added). The hero never uses this — it renders immediately so it cannot delay
 * LCP.
 *
 * `prefers-reduced-motion` is handled in newui.css, which forces `.v2-reveal`
 * visible and cancels the animation, so this component needs no media query.
 */
export const Reveal: React.FC<{
  as?: 'div' | 'section' | 'footer';
  className?: string;
  id?: string;
  /** Stagger, in ms. */
  delay?: number;
  children: React.ReactNode;
}> = ({ as = 'div', className = '', id, delay = 0, children }) => {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const Tag = as as 'div';
  return (
    <Tag
      ref={ref as React.RefObject<HTMLDivElement>}
      id={id}
      className={`v2-reveal ${shown ? 'v2-reveal-in' : ''} ${className}`}
      style={shown && delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
};

/** SSR-safe `matchMedia`. Used by ProductMock for its md and reduced-motion behaviour. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
